import { type LabeledValue } from '@volumental/common';
import { create } from 'zustand';
import { type SizeLocale } from '../api/gql/graphql';

/**
 * SurveyStore contains all user selected data.
 * It should not be persisted between sessions since it needs to
 * change if user retry.
 */

export enum SurveyStep {
  Start = 'start',
  Brand = 'brand_name',
  Gender = 'gender_age',
  Length = 'length_size',
  Width = 'width_size',
  Result = 'result',
}

export interface StartStep {
  step: SurveyStep.Start;
}
export interface BrandStep {
  step: SurveyStep.Brand;
  brand?: LabeledValue<string>;
}
export interface GenderStep {
  step: SurveyStep.Gender;
  brand: LabeledValue<string>;
  gender?: LabeledValue<string>;
}
export interface LengthStep {
  step: SurveyStep.Length;
  brand: LabeledValue<string>;
  gender: LabeledValue<string>;
  length?: LabeledValue<number>;
  locale?: SizeLocale;
}
export interface WidthStep {
  step: SurveyStep.Width;
  brand: LabeledValue<string>;
  gender: LabeledValue<string>;
  length: LabeledValue<number>;
  locale: SizeLocale;
  width?: LabeledValue<number | undefined>;
}
export interface ResultStep {
  step: SurveyStep.Result;
  brand: LabeledValue<string>;
  gender: LabeledValue<string>;
  length: LabeledValue<number>;
  locale: SizeLocale;
  width: LabeledValue<number | undefined>;
}
type Survey =
  | StartStep
  | BrandStep
  | GenderStep
  | LengthStep
  | WidthStep
  | ResultStep;

interface SurveyStore {
  survey: Survey;
  onNext: (input: {
    brand?: LabeledValue<string>;
    gender?: LabeledValue<string>;
    length?: LabeledValue<number>;
    locale?: SizeLocale;
    width?: LabeledValue<number | undefined>;
  }) => void;
  onBack: () => void;
  onEdit: () => void;
  clearSurveyStore: () => void;
}

export const useSurveyStore = create<SurveyStore>((set, get) => ({
  survey: { step: SurveyStep.Start },
  onNext: (by) => {
    const step = get().survey;
    switch (step.step) {
      case SurveyStep.Start: {
        set({
          survey: { step: SurveyStep.Brand },
        });
        break;
      }
      case SurveyStep.Brand: {
        if (by.brand) {
          set({
            survey: { step: SurveyStep.Gender, brand: by.brand },
          });
        }
        break;
      }
      case SurveyStep.Gender: {
        if (by.gender) {
          set({
            survey: {
              ...step,
              step: SurveyStep.Length,
              gender: by.gender,
            },
          });
        }
        break;
      }
      case SurveyStep.Length: {
        if (by.length !== undefined && by.locale) {
          set({
            survey: {
              ...step,
              step: SurveyStep.Width,
              length: by.length,
              locale: by.locale,
            },
          });
        }
        break;
      }
      case SurveyStep.Width: {
        if (by.width) {
          set({
            survey: {
              ...step,
              step: SurveyStep.Result,
              width: by.width,
            },
          });
        }
        break;
      }
      case SurveyStep.Result: {
        break;
      }
    }
  },
  onBack: () => {
    const step = get().survey;
    switch (step.step) {
      case SurveyStep.Brand: {
        set({
          survey: {
            ...step,
            step: SurveyStep.Start,
          },
        });
        break;
      }
      case SurveyStep.Gender: {
        set({
          survey: {
            ...step,
            step: SurveyStep.Brand,
          },
        });
        break;
      }
      case SurveyStep.Length: {
        set({
          survey: {
            ...step,
            step: SurveyStep.Gender,
          },
        });
        break;
      }
      case SurveyStep.Width: {
        set({
          survey: {
            ...step,
            step: SurveyStep.Length,
          },
        });
        break;
      }
      case SurveyStep.Start:
      case SurveyStep.Result: {
        break;
      }
    }
  },
  onEdit: () => {
    const step = get().survey;
    set({ survey: { ...step, step: SurveyStep.Brand } });
  },
  clearSurveyStore: () => {
    set(() => ({
      survey: { step: SurveyStep.Start },
    }));
  },
}));
